import { useEffect, useMemo, useRef, useState } from "react";
import { Text, View } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { createFileRoute } from "@tanstack/react-router";
import { useVirtualizer } from "@tanstack/react-virtual";

import { NowAnchor } from "@/components/timeline/NowAnchor";
import { NowDivider } from "@/components/timeline/NowDivider";
import {
  TimelineItem,
  type TimelineEntryKind,
} from "@/components/timeline/TimelineItem";
import { CompoundSwitcher } from "@/components/CompoundSwitcher";
import { EditDoseSheet } from "@/components/EditDoseSheet";
import { ConfirmDoseSheet } from "@/components/ConfirmDoseSheet";

import { activeAmountAt } from "@/lib/pk/engine";
import { buildDoseModel } from "@/lib/pk/model";
import { ProjectionTimingPicker } from "@/components/ProjectionTimingPicker";
import { useProjectionTiming } from "@/lib/state/useProjectionTiming";
import { useNow } from "@/lib/state/useNow";
import type { ProposedDose } from "@/lib/pk/project";
import { useCompounds } from "@/lib/state/compounds";
import { formatMg, formatElapsedShort, formatDaysShort } from "@/lib/format";
import type { LoggedDose } from "@/lib/state/compounds";

export const Route = createFileRoute("/_tabs/home")({
  component: HomeScreen,
});

type Row =
  | { kind: "label"; id: string; text: string }
  | { kind: "past"; id: string; dose: LoggedDose }
  | { kind: "now"; id: string }
  | { kind: "upcoming"; id: string; proposed: ProposedDose };

const EMPTY_ROWS: Row[] = [];

function HomeScreen() {
  const { active, view, setStatus, deleteDose, updateDose, confirmNew } =
    useCompounds();

  const now = useNow();
  const [timing] = useProjectionTiming();
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [editingDose, setEditingDose] = useState<LoggedDose | null>(null);
  const [confirming, setConfirming] = useState<{
    amountMg: number;
    takeAt: Date;
  } | null>(null);

  // ── Derived model (rows + everything the "now" block needs) ────────────────
  const model = useMemo(() => {
    if (!active || !view) return null;

    const shared = buildDoseModel({
      now,
      doses: [
        ...view.confirmedDoses,
        ...view.pendingDoses,
        ...view.skippedDoses,
      ],
      schedule: active.schedule,
      compound: active.preset,
      count: 6,
      scenario: timing,
    });
    const { takeNowRec, nextScheduledRec, upcoming } = shared;
    const activeMg = activeAmountAt(now, shared.confirmedDoses, active.preset);
    const pastTiles: LoggedDose[] = [
      ...shared.confirmedDoses,
      ...shared.unresolvedDoses,
      ...view.skippedDoses.filter((d) => +d.takenAt <= +now),
    ].sort((a, b) => +a.takenAt - +b.takenAt);

    const mostRecentConfirmed = view.confirmedDoses
      .filter((d) => d.takenAt.getTime() <= now.getTime())
      .slice()
      .sort((a, b) => b.takenAt.getTime() - a.takenAt.getTime())[0];
    const sinceLastLabel = mostRecentConfirmed
      ? formatElapsedShort(mostRecentConfirmed.takenAt, now)
      : undefined;
    const showTakeNow = takeNowRec !== null;
    const pendingCount = shared.unresolvedDoses.length;

    const rows: Row[] = [];
    if (pastTiles.length > 0)
      rows.push({ kind: "label", id: "label-past", text: "Past" });
    for (const d of pastTiles)
      rows.push({ kind: "past", id: `past-${d.id}`, dose: d });
    const nowIndex = rows.length;
    rows.push({ kind: "now", id: "now" });
    if (upcoming.length > 0)
      rows.push({
        kind: "label",
        id: "label-up",
        text: `${shared.scenarioApplied === "now" ? "If logged now" : "Scheduled scenario"} (saved plans included)`,
      });
    upcoming.forEach((p, i) =>
      rows.push({ kind: "upcoming", id: `up-${i}`, proposed: p }),
    );

    const firstPendingIndex = rows.findIndex(
      (r) => r.kind === "past" && r.dose.status === "pending",
    );

    return {
      takeNowRec,
      takeNowUnavailableReason: shared.takeNowUnavailableReason,
      nextScheduledRec,
      activeMg,
      sinceLastLabel,
      showTakeNow,
      pendingCount,
      rows,
      nowIndex,
      firstPendingIndex,
    };
  }, [active, view, now, timing]);

  const rows = model?.rows ?? EMPTY_ROWS;

  const parentRef = useRef<HTMLDivElement | null>(null);
  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: (i) => {
      const r = rowsRef.current[i];
      if (!r) return 104;
      if (r.kind === "now") return 300;
      if (r.kind === "label") return 34;
      return 104;
    },
    getItemKey: (i) => rowsRef.current[i]?.id ?? i,
    overscan: 8,
    paddingStart: 8,
    paddingEnd: 56,
  });

  // Center the list on "now" the first time we see a compound (and on switch).
  const centeredFor = useRef<string | null>(null);
  useEffect(() => {
    if (!active || !model) return;
    if (centeredFor.current === active.id) return;
    const center = () => {
      if (!parentRef.current?.clientHeight) return;
      virtualizer.scrollToIndex(model.nowIndex, { align: "center" });
      centeredFor.current = active.id;
    };
    const raf = requestAnimationFrame(center);
    const t = setTimeout(center, 90);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t);
    };
  }, [active?.id, model?.nowIndex, virtualizer]);

  if (!active || !view || !model) return null;

  const kindOf = (d: LoggedDose): TimelineEntryKind => {
    if (d.status === "confirmed") return "past-confirmed";
    if (d.status === "skipped") return "past-skipped";
    return "past-pending";
  };

  const scrollToPending = () => {
    const idx = model.firstPendingIndex >= 0 ? model.firstPendingIndex : 0;
    virtualizer.scrollToIndex(idx, { align: "center" });
  };

  const persist = async (action: () => Promise<void> | void) => {
    setMutationError(null);
    try {
      await action();
    } catch (error) {
      setMutationError(
        error instanceof Error
          ? error.message
          : "Unable to save. Please retry.",
      );
    }
  };

  const renderRow = (r: Row) => {
    switch (r.kind) {
      case "label":
        return <SectionLabel>{r.text}</SectionLabel>;
      case "past": {
        const d = r.dose;
        return (
          <TimelineItem
            takenAt={d.takenAt}
            amountMg={d.amount}
            defaultDoseMg={active.schedule.defaultDoseMg}
            concentrationMgPerMl={active.concentrationMgPerMl}
            kind={kindOf(d)}
            onConfirm={
              d.status === "pending"
                ? () => void persist(() => setStatus(d.id, "confirmed"))
                : undefined
            }
            onSkip={
              d.status === "pending"
                ? () => void persist(() => setStatus(d.id, "skipped"))
                : undefined
            }
            onPress={() => setEditingDose(d)}
          />
        );
      }
      case "now":
        return (
          <View>
            <NowDivider at={now} sinceLastLabel={model.sinceLastLabel} />
            <NowAnchor
              takeNowRec={model.takeNowRec}
              unavailableReason={model.takeNowUnavailableReason}
              nextScheduledRec={model.nextScheduledRec}
              concentrationMgPerMl={active.concentrationMgPerMl}
              blockedByPending={
                model.pendingCount > 0
                  ? { count: model.pendingCount }
                  : undefined
              }
              showTakeNow={model.showTakeNow}
              onConfirm={() => {
                const takeAt = new Date();
                const rec = buildDoseModel({
                  now: takeAt,
                  doses: [
                    ...view.confirmedDoses,
                    ...view.pendingDoses,
                    ...view.skippedDoses,
                  ],
                  schedule: active.schedule,
                  compound: active.preset,
                }).takeNowRec;
                if (rec?.kind === "take") {
                  setConfirming({ amountMg: rec.doseMg, takeAt: rec.takeAt });
                }
              }}
              onAddLikeThis={() => {
                const visibleRec = model.showTakeNow
                  ? model.takeNowRec
                  : model.nextScheduledRec;
                const amountMg =
                  visibleRec?.kind === "take"
                    ? visibleRec.doseMg
                    : active.schedule.defaultDoseMg;
                setConfirming({ amountMg, takeAt: new Date() });
              }}
              onScrollToPending={scrollToPending}
            />
          </View>
        );
      case "upcoming":
        if (r.proposed.kind === "skip")
          return (
            <View className="my-2 rounded-xl border border-border p-4">
              <Text className="text-sm text-text">
                No injection · {r.proposed.takeAt.toLocaleString()}
              </Text>
              <Text className="mt-1 text-xs text-textDim">
                {r.proposed.reason}
              </Text>
            </View>
          );
        return (
          <TimelineItem
            takenAt={r.proposed.takeAt}
            amountMg={r.proposed.doseMg}
            defaultDoseMg={active.schedule.defaultDoseMg}
            concentrationMgPerMl={active.concentrationMgPerMl}
            kind="upcoming-proposed"
            onPress={() => {
              if (r.proposed.kind === "planned") {
                const plan = [
                  ...view.pendingDoses,
                  ...view.confirmedDoses,
                ].find((d) => +d.takenAt === +r.proposed.takeAt);
                if (plan) setEditingDose(plan);
              } else
                setConfirming({
                  amountMg: r.proposed.doseMg,
                  takeAt: r.proposed.takeAt,
                });
            }}
          />
        );
    }
  };

  const virtualItems = virtualizer.getVirtualItems();

  return (
    <SafeAreaView className="flex-1 bg-bg" edges={["top"]}>
      <View className="flex-row items-center justify-between px-5 pb-3 pt-2">
        <View>
          <Text className="text-xl font-semibold lowercase tracking-tight text-text">
            halvn
          </Text>
          <Text className="mt-0.5 text-xs text-textMuted">
            {formatMg(model.activeMg)} modeled active · every{" "}
            {formatDaysShort(active.schedule.cadenceHours)}
          </Text>
        </View>
        <CompoundSwitcher />
      </View>

      <View className="px-5 pb-3">
        <ProjectionTimingPicker />
      </View>
      {mutationError && (
        <View role="alert" className="mx-5 mb-2 rounded-xl bg-warning/10 p-3">
          <Text className="text-sm text-warning">{mutationError}</Text>
        </View>
      )}
      <Text className="px-5 pb-2 text-[11px] leading-4 text-textMuted">
        Model scenarios match a reference point, not a blood level or prescribed
        dose. Future entries remain plans until confirmed.
      </Text>
      <div
        ref={parentRef}
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        <div
          style={{
            height: virtualizer.getTotalSize(),
            width: "100%",
            position: "relative",
          }}
        >
          {virtualItems.map((vi) => {
            const r = rows[vi.index];
            if (!r) return null;
            return (
              <div
                key={vi.key}
                data-index={vi.index}
                ref={virtualizer.measureElement}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  transform: `translateY(${vi.start}px)`,
                  paddingLeft: 20,
                  paddingRight: 20,
                  boxSizing: "border-box",
                }}
              >
                {renderRow(r)}
              </div>
            );
          })}
        </div>
      </div>

      <EditDoseSheet
        visible={editingDose !== null}
        dose={editingDose}
        concentrationMgPerMl={active.concentrationMgPerMl}
        onClose={() => setEditingDose(null)}
        onSave={(patch) => {
          if (editingDose) return updateDose(editingDose.id, patch);
        }}
        onDelete={() => {
          if (editingDose) return deleteDose(editingDose.id);
        }}
      />

      <ConfirmDoseSheet
        visible={confirming !== null}
        initialAmountMg={confirming?.amountMg ?? 0}
        initialTakeAt={confirming?.takeAt ?? now}
        concentrationMgPerMl={active.concentrationMgPerMl}
        onClose={() => setConfirming(null)}
        onConfirm={(amount, takenAt) => confirmNew(amount, takenAt)}
      />
    </SafeAreaView>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <Text className="mb-1 mt-4 text-[10px] font-medium uppercase tracking-wider text-textMuted">
      {children}
    </Text>
  );
}
